import config from '@conf'
import { Service } from './service'
import { ManageContractRequestBuilder,
  ManageContractBuilder,
  ReviewRequestBuilder,
  ManageInvoiceRequestBuilder,
  xdr
} from 'tokend-js-sdk'

export class ContractsService extends Service {
  /**
   * Creates contract request
   *
   * @param {object} opts
   * @param {string} opts.customer - contract customer
   * @param {string} opts.escrow - contract escrow
   * @param {string} opts.startTime - contract start time
   * @param {string} opts.endTime - contract end time
   * @param {object} opts.details - contract details
   * @param {string} [opts.source] - The source account for the contract request. Defaults to the transaction's source account.
   * @return {TransactionResponseBuilder}
   */
  createContractRequest (opts) {
    const operation = ManageContractRequestBuilder.createContractRequest(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  /**
   * Removes contract request
   *
   * @param {object} opts
   * @param {string} opts.requestId - contract request id to remove
   * @param {string} [opts.source] - The source account for the contract request. Defaults to the transaction's source account.
   *
   * @return {TransactionResponseBuilder}
   */
  removeContractRequest (opts) {
    const operation = ManageContractRequestBuilder.removeContractRequest(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

    /**
   * Loads created sales with specified filters
   * @param filters
   * @param {string} filters.contractNumber - base token code (match case only)
   * @param {number} filters.startTime - if true, loads all sales with open state (includes upcoming)
   * @param {number} filters.endTime - if true, loads only upcoming sales
   * @param {number} filters.sortBy - sort type {@link saleSortTypes}
   * @param {string} filters.name - name of the sale (works with substring)
   * @param {string} filters.order - default asc/desc
   *
   * @returns {Promise<object>} - Promise object representing sales
   */
  loadContracts (filters) {
    return this._horizonRequestBuilder
      .contracts()
      .forCounterparty(filters.counterparty)
      .forSource(filters.source)
      .forContractNumber(filters.contractNumber)
      .forStartTime(filters.startTime)
      .forEndTime(filters.endTime)
      .byDisputingState(filters.isDisputing)
      .byCompletedState(filters.isCompleted)
      .limit(config.TRANSACTIONS_PER_PAGE)
      .callWithSignature(this._keypair)
  }

  loadContractById (id) {
    return this._horizonRequestBuilder.contracts()
      .contract(id)
      .callWithSignature(this._keypair)
  }

  signContract (opts) {
    const operation = ReviewRequestBuilder.reviewContractRequest({
      requestID: opts.id,
      requestHash: opts.hash,
      requestType: typeof opts.details.request_type_i === 'undefined' ? opts.details.requestTypeI : opts.details.request_type_i,
      source: this._accountId,
      action: xdr.ReviewRequestOpAction.approve().value,
      reason: '',
      details: {}
    })
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  rejectContract (opts, rejectReason) {
    const operation = ReviewRequestBuilder.reviewContractRequest({
      requestID: opts.id,
      requestHash: opts.hash,
      requestType: typeof opts.details.request_type_i === 'undefined' ? opts.details.requestTypeI : opts.details.request_type_i,
      source: this._accountId,
      action: xdr.ReviewRequestOpAction.permanentReject().value,
      reason: rejectReason,
      details: {}
    })
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  startDispute (opts) {
    const operation = ManageContractBuilder.startDispute(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  resolveDispute (opts) {
    const operation = ManageContractBuilder.resolveDispute(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  createInvoiceRequest (opts) {
    const operation = ManageInvoiceRequestBuilder.createInvoiceRequest(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  removeInvoiceRequest (opts) {
    const operation = ManageInvoiceRequestBuilder.removeInvoiceRequest(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  confirmInvoiceRequest (opts, billPayDetails) {
    const operation = ReviewRequestBuilder.reviewInvoiceRequest({
      requestID: opts.id,
      requestHash: opts.hash,
      requestType: typeof opts._record.details.request_type_i === 'undefined' ? opts._record.details.requestTypeI : opts._record.details.request_type_i,
      source: this._accountId,
      action: xdr.ReviewRequestOpAction.approve().value,
      billPayDetails: billPayDetails,
      reason: ''
    })
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  rejectInvoiceRequest (opts, billPayDetails, rejectReason) {
    const operation = ReviewRequestBuilder.reviewInvoiceRequest({
      requestID: opts.id,
      requestHash: opts.hash,
      requestType: typeof opts._record.details.request_type_i === 'undefined' ? opts._record.details.requestTypeI : opts._record.details.request_type_i,
      source: this._accountId,
      action: xdr.ReviewRequestOpAction.permanentReject().value,
      billPayDetails: billPayDetails,
      reason: rejectReason
    })
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  confirmCompleted (opts) {
    const operation = ManageContractBuilder.confirmCompleted(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }

  addDetails (opts) {
    const operation = ManageContractBuilder.addDetails(opts)
    return this._operationBuilder
      .operation()
      .add(operation)
      .submit(this._accountId, this._keypair)
  }
}

export const contractsService = new ContractsService()



// WEBPACK FOOTER //
// ./src/js/services/contracts.service.js